import { convexQuery } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

const formatRate = (ratePercent: number | null): string => {
  if (ratePercent === null) return '—'
  return `${Math.round(ratePercent)}%`
}

const formatScore = (score: number | null): string => {
  if (score === null) return '—'
  return String(Math.round(score))
}

const STATUS_CLASS = {
  draft:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  active:
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  paused:
    'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
} as const

const STATUS_LABEL_KEY = {
  draft: 'jobs.statusDraft',
  active: 'jobs.statusActive',
  paused: 'jobs.statusPaused',
} as const

export const AnalyticsSection = () => {
  const { t } = useTranslation()
  const { data: analytics } = useQuery(
    convexQuery(api.features.jobs.jobs.getMyOrgAnalytics, {}),
  )

  if (!analytics || analytics.jobs.length === 0) return null

  const { totals, jobs } = analytics

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-sm font-semibold text-primary/70">
        {t('dashboard.analytics.title')}
      </h2>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="flex flex-col gap-1 rounded-lg border border-border bg-card p-4">
          <span className="text-2xl font-semibold text-primary">
            {totals.invited}
          </span>
          <span className="text-xs text-primary/50">
            {t('dashboard.statsInvited')}
          </span>
        </div>
        <div className="flex flex-col gap-1 rounded-lg border border-border bg-card p-4">
          <span className="text-2xl font-semibold text-primary">
            {totals.completed}
          </span>
          <span className="text-xs text-primary/50">
            {t('dashboard.statsCompleted')}
          </span>
        </div>
        <div className="flex flex-col gap-1 rounded-lg border border-border bg-card p-4">
          <span className="text-2xl font-semibold text-primary">
            {formatRate(totals.completionRatePercent)}
          </span>
          <span className="text-xs text-primary/50">
            {t('dashboard.analytics.completionRate')}
          </span>
        </div>
        <div className="flex flex-col gap-1 rounded-lg border border-border bg-card p-4">
          <span className="text-2xl font-semibold text-primary">
            {formatScore(totals.averageScore)}
          </span>
          <span className="text-xs text-primary/50">
            {t('dashboard.statsAvgScore')}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary">
              <th className="px-4 py-2.5 text-left text-xs font-medium text-primary/60">
                {t('dashboard.analytics.jobTitle')}
              </th>
              <th className="px-4 py-2.5 text-center text-xs font-medium text-primary/60">
                {t('dashboard.analytics.status')}
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-primary/60">
                {t('dashboard.statsInvited')}
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-primary/60">
                {t('dashboard.statsCompleted')}
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-primary/60">
                {t('dashboard.statsAdvanced')}
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-primary/60">
                {t('dashboard.analytics.completionRate')}
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-primary/60">
                {t('dashboard.statsAvgScore')}
              </th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job, index) => (
              <tr
                key={job.jobId}
                className={
                  index < jobs.length - 1 ? 'border-b border-border' : ''
                }
              >
                <td className="px-4 py-3 text-sm text-primary">
                  {job.jobTitle}
                </td>
                <td className="px-4 py-3 text-center">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[job.status]}`}
                  >
                    {t(STATUS_LABEL_KEY[job.status])}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-primary">
                  {job.invited}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-primary">
                  {job.completed}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-primary">
                  {job.advanced}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-primary">
                  {formatRate(job.completionRatePercent)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-primary">
                  {formatScore(job.averageScore)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
